import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { getAdminSettings, updateAdminSettings } from '../services/api';
import { Bell, Mail } from 'lucide-react';

const SettingsModal = ({ isOpen, onClose }) => {
  const { user, userType, updatePassword, updateUsername } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  
  // Email notification settings (Admin only)
  const [notificationEmail, setNotificationEmail] = useState('');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  useEffect(() => {
    if (isOpen && userType === 'admin' && user?.id) {
      loadAdminSettings();
    }
  }, [isOpen, userType, user]);

  const loadAdminSettings = async () => {
    try {
      const settings = await getAdminSettings(user.id);
      setNotificationEmail(settings.notificationEmail || '');
      setEmailNotificationsEnabled(settings.emailNotificationsEnabled || false);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (currentPassword !== user.password) {
      toast({
        title: 'Error',
        description: 'Current password is incorrect',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    await updatePassword(newPassword);
    toast({
      title: 'Success',
      description: 'Password updated successfully'
    });
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleUsernameChange = async () => {
    if (!newUsername.trim()) {
      toast({
        title: 'Error',
        description: 'Username cannot be empty',
        variant: 'destructive'
      });
      return;
    }

    await updateUsername(newUsername);
    toast({
      title: 'Success',
      description: 'Username updated successfully'
    });
    setNewUsername('');
  };

  const handleSaveNotificationSettings = async () => {
    if (emailNotificationsEnabled && !notificationEmail) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive'
      });
      return;
    }

    // Validate email format
    if (notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notificationEmail)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    setLoadingSettings(true);
    try {
      await updateAdminSettings(user.id, {
        notificationEmail,
        emailNotificationsEnabled
      });
      toast({
        title: 'Success',
        description: 'Notification settings saved successfully!',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setLoadingSettings(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Account Settings</DialogTitle>
          <DialogDescription>Manage your account preferences and notifications</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Email Notifications Section (Admin Only) */}
          {userType === 'admin' && (
            <div className="space-y-4 pb-6 border-b">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold text-lg">Payment Notifications</h3>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  Get instant email notifications when customers complete purchases. 
                  Each notification includes order details and confirmation code.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email-notifications">Enable Email Notifications</Label>
                    <p className="text-sm text-gray-600">Receive alerts for every completed transaction</p>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={emailNotificationsEnabled}
                    onCheckedChange={setEmailNotificationsEnabled}
                  />
                </div>

                <div>
                  <Label htmlFor="notification-email">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4" />
                      Notification Email Address
                    </div>
                  </Label>
                  <Input
                    id="notification-email"
                    type="email"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    placeholder="your-email@gmail.com"
                    disabled={!emailNotificationsEnabled}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Enter the Gmail address where you want to receive payment notifications
                  </p>
                </div>

                <Button 
                  onClick={handleSaveNotificationSettings} 
                  className="w-full"
                  disabled={loadingSettings}
                >
                  {loadingSettings ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </div>
            </div>
          )}

          {/* Change Password Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Change Password</h3>
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            <Button onClick={handlePasswordChange} className="w-full">
              Update Password
            </Button>
          </div>

          {/* Change Username Section (Admin Only) */}
          {userType === 'admin' && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-lg">Change Username</h3>
              <div>
                <Label htmlFor="new-username">New Username</Label>
                <Input
                  id="new-username"
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter new username"
                />
              </div>
              <Button onClick={handleUsernameChange} className="w-full" variant="outline">
                Update Username
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
